import * as React from "react";
import * as ReactDOM from "react-dom";
import { Button } from "@progress/kendo-react-buttons";
import { Window } from "@progress/kendo-react-dialogs";
import { EditorUtils } from "@progress/kendo-react-editor";
import { TabStrip, TabStripTab } from "@progress/kendo-react-layout";
import { Upload } from "@progress/kendo-react-upload";
import config from "../../env.config";
import { insertImageFiles } from "./upload-image-util";

export const InsertImageDialog = (props) => {
  const [selected, setSelected] = React.useState(0);
  const [files, setFiles] = React.useState([]);
  let src;
  // let altText;
  let title;
  let width;
  let height;

  const onTabSelect = (event) => {
    setFiles([]);
    setSelected(event.selected);
  };

  const onClose = () => {
    props.onClose.call(undefined);
  };

  const onAddFiles = (event) => {
    setFiles(event.newState.map((f) => f.getRawFile && f.getRawFile()).filter((f) => f));
  };

  /**
   * Processes image attributes by filtering out null/empty values
   * and ensuring the 'image-border' class is added.
   *
   * @param {Object} attributes - The image attributes.
   * @param {string} attributes.src - The image source URL.
   * @param {string} attributes.title - The image title.
   * @param {string} attributes.width - The image width.
   * @param {string} attributes.height - The image height.
   * @returns {Object} - Processed attributes with valid values.
   */
  const processImageAttributes = ({ src, title, width, height }) => {
    const data = { src, title, width, height };
    // Filter out null or empty values
    const attrs = Object.keys(data)
      .filter((key) => data[key])
      .reduce((acc, key) => {
        acc[key] = data[key];
        return acc;
      }, {});
    // Ensure 'image-border' class is added
    attrs.class = attrs.class ? `${attrs.class} image-border`.trim() : "image-border";
    return attrs;
  };

  /**
   * Inserts a new image node into the editor at the current selection.
   *
   * @param {Object} params - The function parameters.
   * @param {Object} params.view - The editor's view instance.
   * @param {Object} params.nodeType - The ProseMirror node type for images.
   * @param {Object} params.attrs - The processed attributes for the image.
   */
  const insertNewImage = ({ view, nodeType, attrs }) => {
    const newImage = nodeType.createAndFill(attrs);
    if (newImage) {
      EditorUtils.insertNode(view, newImage, true);
    }
  };

  /**
   * Handles the image insertion process.
   * - Processes image attributes
   * - Determines whether to insert an image from a file or a URL
   * - Ensures images get the 'image-border' class
   * - Focuses the editor after insertion
   */
  const onInsert = () => {
    const { view, imageNode } = props;
    const nodeType = view.state.schema.nodes[imageNode];
    const position = null; // Let the editor decide the position
    // Process image attributes (filter out nulls & add 'image-border' class)
    const attrs = processImageAttributes({
      src: src?.value,
      title: title?.value,
      width: width?.value,
      height: height?.value,
    });
    // If files are provided, insert the image from the file system
    if (files.length) {
      insertImageFiles({ view, files, nodeType, position, attrs });
    } else {
      insertNewImage({ view, nodeType, attrs });
    }
    view.focus();
    onClose();
  };

  const { view, imageNode } = props;
  const state = view && view.state;
  let attrs = {};
  if (state && state.selection.node && state.selection.node.type === state.schema.nodes[imageNode]) {
    attrs = state.selection.node.attrs;
  }

  const fields = (
    <React.Fragment>
      <div className='form-row'>
        <div className='form-group col-md-6'>
          <label>Width (px)</label>
          <input className='form-control w-100' id='k-editor-image-width' defaultValue={attrs.width} ref={(e) => (width = e)} />
        </div>
        <div className='form-group col-md-6'>
          <label>Height (px)</label>
          <input className='form-control w-100' id='k-editor-image-height' defaultValue={attrs.height} ref={(e) => (height = e)} />
        </div>
      </div>
    </React.Fragment>
  );
  const buttons = (
    <div
      className={"text-right"}
      style={{
        clear: "both",
      }}>
      <Button onClick={onClose} className='btn btn-ghost mr-2'>
        Cancel
      </Button>
      <Button onClick={onInsert} themeColor={"primary"} className='btn btn-primary'>
        Insert
      </Button>
    </div>
  );

  return ReactDOM.createPortal(
    <Window title='Insert Image' className="remove-min-max-icon" onClose={onClose} initialWidth={500} initialHeight={375}>
      <TabStrip selected={selected} onSelect={onTabSelect} animation={false}>
        {Object.entries(attrs).length === 0 && (
          <TabStripTab title='Upload'>
            <div className='form-group mt-2'>
              <label>Image</label>
              <Upload
                batch={false}
                multiple={true}
                defaultFiles={[]}
                withCredentials={false}
                accept='image/*'
                saveUrl={`${config.BASE_URL.BASE_API_URL}/common/uploadEditorS3Iframe`}
                removeUrl={"https://demos.telerik.com/kendo-ui/service-v4/upload/remove"}
                onAdd={onAddFiles}
              />
            </div>
            {fields}
            {buttons}
          </TabStripTab>
        )}
        <TabStripTab title='By URL'>
          <div className='form-group mt-2'>
            <label>Web address</label>
            <input
              type='text'
              className='form-control w-100'
              id='k-editor-image-url'
              defaultValue={attrs.src}
              disabled={/^data:image/.test(attrs.src || "")}
              ref={(e) => (src = e)}
              autoFocus={true}
            />
          </div>
          {fields}
          {buttons}
        </TabStripTab>
      </TabStrip>
      <style>{`.k-dropzone { width: 100%; }`}</style>
    </Window>,
    document.body
  );
};
